/*
 * Modern Checkup — Cart Guard
 * ---------------------------------------------------------------------------
 * Enforces the kit + membership pairing rules on every cart mutation:
 *   1. A kit can never be sold without a membership — adding a kit auto-adds
 *      the membership.
 *   2. The only valid non-empty cart is EXACTLY 1 kit + 1 membership.
 *      Never 2 kits, never 2 memberships.
 *   3. Removing the kit removes the membership too (bundle-lock; configurable).
 *   4. Any cart update re-aligns the cart to these rules.
 *
 * Mechanism: a single idempotent reconcile() reads /cart.js and issues the
 * minimal /cart/change.js|/cart/add.js calls to reach the valid state. We wrap
 * window.fetch (and XHR) so any cart mutation AWAITS reconcile before it
 * resolves — which is what makes the buy box's "add kit then redirect to
 * /checkout" path add the membership before the redirect fires.
 *
 * Config comes from window.MC_CART_GUARD (see snippets/mc-cart-guard.liquid).
 *
 * NOTE: This is client-side enforcement — correct for every real user path but
 * bypassable via hand-crafted API calls. For tamper-proof enforcement pair this
 * with a Cart/Checkout Validation Shopify Function.
 */
(function () {
  'use strict';

  var CFG = window.MC_CART_GUARD || {};
  var KIT = Number(CFG.kit || 0);
  var MEM = Number(CFG.membership || 0);
  var MEM_PLAN = CFG.membershipSellingPlan ? Number(CFG.membershipSellingPlan) : null;
  var BUNDLE_LOCK = CFG.bundleLock !== false; // default true: no membership without a kit
  var MAX_KIT = Number(CFG.maxKit || 1);
  var MAX_MEM = Number(CFG.maxMembership || 1);

  if (!KIT || !MEM || typeof window.fetch !== 'function') return; // misconfigured — do nothing

  var origFetch = window.fetch.bind(window);
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  var busy = false;    // reconcile in flight
  var pending = false; // a mutation arrived while busy — run once more after

  function getCart() {
    return origFetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function jpost(url, data) {
    return origFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(data)
    }).then(function (r) { return r.json().catch(function () { return {}; }); });
  }

  // Clamp every line of a variant down to `target` total qty (keep the first line).
  // Returns a chained promise. Calls onChange() when it actually mutates.
  function clampVariant(chain, lines, target, onChange) {
    if (!lines.length) return chain;
    // Remove any extra lines of the same variant beyond the first.
    for (var k = 1; k < lines.length; k++) {
      (function (key) {
        chain = chain.then(function () { onChange(); return jpost('/cart/change.js', { id: key, quantity: 0 }); });
      })(lines[k].key);
    }
    var first = lines[0];
    if (first.quantity !== target) {
      chain = chain.then(function () { onChange(); return jpost('/cart/change.js', { id: first.key, quantity: target }); });
    }
    return chain;
  }

  // Bring the cart to the single valid state. Idempotent.
  function reconcile() {
    if (busy) { pending = true; return Promise.resolve({ changed: false }); }
    busy = true;
    var changed = false;
    var mark = function () { changed = true; };

    return getCart().then(function (cart) {
      var items = (cart && cart.items) || [];
      var kitLines = items.filter(function (i) { return i.id === KIT; });
      var memLines = items.filter(function (i) { return i.id === MEM; });
      var kitQty = kitLines.reduce(function (s, i) { return s + i.quantity; }, 0);
      var memQty = memLines.reduce(function (s, i) { return s + i.quantity; }, 0);

      var desiredKit = kitQty > 0 ? Math.min(1, MAX_KIT) : 0;         // 1 if any kit present, else 0
      var desiredMem;
      if (desiredKit >= 1) desiredMem = Math.min(1, MAX_MEM);          // kit present -> exactly 1 membership
      else desiredMem = BUNDLE_LOCK ? 0 : Math.min(memQty, MAX_MEM);   // no kit -> drop membership (bundle-lock)

      var chain = Promise.resolve();

      // 1) Clamp the kit to its target (collapses 2 kits -> 1).
      chain = clampVariant(chain, kitLines, desiredKit, mark);

      // 2) Add the membership if a kit is present and none is in the cart yet.
      if (desiredMem >= 1 && memQty === 0) {
        chain = chain.then(function () {
          mark();
          var item = { id: MEM, quantity: 1 };
          if (MEM_PLAN) item.selling_plan = MEM_PLAN;
          return jpost('/cart/add.js', { items: [item] });
        });
      }

      // 3) Clamp/remove any existing membership lines to the target.
      if (memQty > 0) {
        chain = clampVariant(chain, memLines, desiredMem, mark);
      }

      return chain;
    }).then(finish, finish);

    function finish() {
      busy = false;
      if (pending) { pending = false; return reconcile().then(function () { return { changed: changed }; }); }
      return { changed: changed };
    }
  }

  // Best-effort UI sync after a corrective change (cart data is always correct
  // regardless; this just keeps the visible cart/count in step).
  function refreshUI(changed) {
    if (!changed) return;
    getCart().then(function (cart) {
      try {
        var count = cart.item_count;
        document.querySelectorAll('.cart-count-bubble').forEach(function (b) {
          var span = b.querySelector('span[aria-hidden="true"]') || b.querySelector('span');
          if (span) span.textContent = count;
        });
        document.querySelectorAll('[data-cart-count]').forEach(function (el) { el.textContent = count; });
      } catch (e) {}
      try {
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'mc-cart-guard', cartData: cart });
        }
      } catch (e) {}
    });
    // On the cart page, reload once so corrected lines are shown (reconcile is
    // idempotent, so the post-reload pass makes no further change → no loop).
    if (location.pathname.replace(/\/+$/, '') === '/cart') location.reload();
  }

  // Wrap fetch: cart-mutating requests await reconcile before resolving.
  window.fetch = function (input, init) {
    var url = (typeof input === 'string') ? input : (input && input.url) || '';
    var call = origFetch(input, init);
    if (busy || !CART_MUTATION.test(url)) return call; // internal / non-cart calls pass straight through
    return call.then(function (res) {
      return reconcile().then(function (r) { refreshUI(r.changed); return res; }, function () { return res; });
    });
  };

  // Wrap XHR too, in case any script uses it for cart calls.
  (function () {
    var open = XMLHttpRequest.prototype.open;
    var send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__mcCart = CART_MUTATION.test(url || '');
      return open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      if (xhr.__mcCart && !busy) {
        xhr.addEventListener('loadend', function () { reconcile().then(function (r) { refreshUI(r.changed); }); });
      }
      return send.apply(this, arguments);
    };
  })();

  // Align any stale cart (e.g. a kit-only cart created before this shipped).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reconcile().then(function (r) { refreshUI(r.changed); }); });
  } else {
    reconcile().then(function (r) { refreshUI(r.changed); });
  }

  // Expose for debugging / manual use.
  window.MC_CART_GUARD.reconcile = reconcile;
})();
